/**
 * 吹き出しラベルを「文字単位」で描くときの寸法と配置の計算。
 *
 * 値はすべて共通ライブラリの `SpeechBalloonLabelGenerator`（従来のラベル画像）に揃えてある。
 * 従来のラベルは枠線を持たず、角丸の矩形に影を落とし、しっぽを角丸の曲線で描いている。
 *
 * 描画そのものは `custom-layers/SpeechBalloonLabelLayer.js` が行う。
 * ここは地図ライブラリに依存しない計算だけを持つ。
 */

/**
 * 影（ぼかしが無いので、同じ形をずらして敷けば再現できる）
 * @type {Object}
 */
export const SHADOW = {
  color: [0, 0, 0, 26],
  offsetY: 2,
}

/**
 * しっぽの寸法と曲がり具合
 * @type {Object}
 */
export const BEAK = {
  size: 5,
  rootRatio: 0.4,
  tipRatio: 7 / 30,
}

/**
 * 吹き出しの見た目
 * @type {Object}
 */
export const BALLOON = {
  borderRadius: 4,
  // [左, 上, 右, 下]
  padding: [8, 5, 8, 5],
}

/**
 * 地図ライブラリが1行に取る高さの倍率
 * @type {Number}
 */
export const ROW_HEIGHT_SCALE = 1.2

/**
 * 行の高さ
 *
 * 地図ライブラリは1行を「文字サイズ × ROW_HEIGHT_SCALE」の高さで扱い、
 * そこへさらに行の高さを掛ける。従来のラベル画像は
 * 「文字サイズ＋上下の余白」ちょうどの高さなので、倍率を打ち消す値にすると一致する
 * @type {Number}
 */
export const LINE_HEIGHT = 1 / ROW_HEIGHT_SCALE

/**
 * 基準点からラベルの文字までのずらし量
 *
 * 従来のラベル画像は下端（影を含む）を基準点に合わせている。
 * そのためしっぽの先端は影のぶん上、吹き出しの下辺はさらにしっぽのぶん上になる
 * @type {Array.<Number>}
 */
export const LABEL_PIXEL_OFFSET = [
  0,
  -(SHADOW.offsetY + BEAK.size + BALLOON.padding[3]),
]

/**
 * 吹き出しの下辺の位置（基準点から上に何px）
 * @type {Number}
 */
export const BALLOON_BOTTOM_Y = LABEL_PIXEL_OFFSET[1] + BALLOON.padding[3]

/**
 * 2段目の先頭アイコンの大きさが文字サイズに対して占める割合
 *
 * 1行の高さは文字サイズちょうどなので、同じ大きさのアイコンを置くと
 * 行いっぱいに広がって1段目の文字に接する。少し小さくして上下に余白を残す
 * @type {Number}
 */
export const ICON_SIZE_SCALE = 0.8

/**
 * 文字サイズに合わせた2段目のアイコンの大きさを返す
 * @param {Number} fontSize 文字サイズ(px)
 * @returns {Number} アイコンの大きさ(px)
 */
export const getIconSize = (fontSize) =>
  Math.round((fontSize || 0) * ICON_SIZE_SCALE)

/**
 * 2段目のアイコンぶんに空ける空白の数と幅を求める
 *
 * 吹き出しの背景は文字の幅で決まるため、アイコンを文字の外に置くと枠からはみ出す。
 * 値の行の先頭にアイコンぶんの空白を入れ、その空白の位置にアイコンを描く
 * @param {Number} spaceWidth 空白1つの幅(px)
 * @param {Number} iconSize アイコンの大きさ(px)
 * @param {Number} iconGap アイコンと文字の間隔(px)
 * @returns {Object} 空白の数と合計の幅
 */
export const getIconSpaces = (spaceWidth, iconSize, iconGap) => {
  if (!spaceWidth || spaceWidth <= 0) return { count: 0, width: 0 }
  const count = Math.ceil((iconSize + iconGap) / spaceWidth)
  return { count, width: count * spaceWidth }
}

/**
 * 値の行の前後に、重ねて描くもののぶんの空白を入れた文字列を返す
 *
 * 先頭は表示項目のアイコン、末尾はステイタスの色の丸のぶん
 * @param {String} text ラベルの文字列（改行で複数行）
 * @param {Number} spaceCount 先頭に空ける空白の数
 * @param {Number} trailingSpaceCount 末尾に空ける空白の数
 * @returns {String} 文字列
 */
export const withIconSpaces = (text, spaceCount, trailingSpaceCount = 0) => {
  const value = `${text ?? ''}`
  // 文字が無いときはラベル自体を出さないので、空白だけを入れない
  if ((!spaceCount && !trailingSpaceCount) || !value) return value
  const lines = value.split('\n')
  const last = lines[lines.length - 1]
  lines[lines.length - 1] =
    ' '.repeat(spaceCount || 0) + last + ' '.repeat(trailingSpaceCount || 0)
  return lines.join('\n')
}

/**
 * 最終行の文字の中心が基準点から何px上かを返す
 *
 * 文字は行の中央に置かれるので、下端から最終行の中央までは
 * 「文字サイズ × 行の高さの倍率 ×（行の高さ - 0.5）」になる
 * @param {Number} fontSize 文字サイズ
 * @param {Number} lineHeight 行の高さ
 * @returns {Number} 基準点からのずらし量
 */
export const getLastRowCenterY = (fontSize, lineHeight) =>
  LABEL_PIXEL_OFFSET[1] - fontSize * ROW_HEIGHT_SCALE * (lineHeight - 0.5)

/**
 * 2段目のアイコンの位置を返す（値の行の先頭に空けた空白の左端）
 *
 * 空白は「アイコン＋文字との間隔」ぶん空けてある。アイコンを空白の中央に置くと
 * 間隔が左右に割れて文字に近づくので、左端に寄せて残りを文字との間隔にする
 * @param {Number} spacesWidth 空白の合計の幅
 * @param {Number} valueLineWidth 値の行の文字幅（空白を含まない）
 * @param {Number} centerY 縦のずらし量
 * @param {Number} [iconSize] アイコンの大きさ。省略すると空白の中央に置く
 * @returns {Array.<Number>} ずらし量
 */
export const getIconOffset = (
  spacesWidth,
  valueLineWidth,
  centerY,
  iconSize
) => {
  // 文字は中央寄せなので、行の左端からアイコンの中心までを求める
  const totalWidth = spacesWidth + valueLineWidth
  const toIconCenter = iconSize ? iconSize / 2 : spacesWidth / 2
  return [-totalWidth / 2 + toIconCenter, centerY]
}

/**
 * 末尾に重ねる色の丸の位置を返す（値の行の末尾に空けた空白の中央）
 * @param {Number} spacesWidth 末尾に空けた空白の合計の幅
 * @param {Number} lineWidth 値の行の文字幅（前後の空白を含む）
 * @param {Number} centerY 縦のずらし量
 * @returns {Array.<Number>} ずらし量
 */
export const getDotOffset = (spacesWidth, lineWidth, centerY) => [
  lineWidth / 2 - spacesWidth / 2,
  centerY,
]

/**
 * ラベルの文字数を上限で省略する
 * 仕様: ユーザー名32文字がラベルの最大サイズ。超過分は省略する
 * @param {String} text 文字列
 * @param {Number} maxLength 上限の文字数
 * @returns {String} 省略後の文字列
 */
export const truncateLabel = (text, maxLength) => {
  const value = `${text ?? ''}`
  if (!maxLength || value.length <= maxLength) return value
  return `${value.slice(0, maxLength)}...`
}
